/**
 * The report menu.
 *
 * The trigger is corner-anchored so it cannot be overshot, but a full ring will not
 * fit around a corner — half the petals fall off the screen. So the ring opens in
 * the middle of the screen and accepts either gesture:
 *
 *   press-drag-release  — arm by direction from the ring centre, no aim required
 *   press, release, tap — for anyone who lifts their thumb first
 *
 * Selection is still direction-only once the finger is inside the ring, and
 * releasing in the hub or outside the ring does nothing at all. There is no
 * confirmation dialog anywhere; the 6-second undo bar is the safety net.
 */
import { HAZARDS, RADIAL, REPORTABLE_TYPES, type HazardType, type RadialProfile } from "../../shared/hazards.js";
import { glyphMark } from "../lib/marks.js";

const HUB_R = 54;
const PETAL_R = 118;
const PETAL_SIZE = 106;
/** Outer edge of the live band. Past this the finger has left the menu. */
const BAND_OUTER = PETAL_R + 74;

export type RadialResult = HazardType | "more" | null;

export class Radial {
  private armed: HazardType | null = null;
  private centre = { x: 0, y: 0 };
  private petals: { type: HazardType; el: HTMLElement; angle: number }[] = [];
  private resolve: ((r: RadialResult) => void) | null = null;
  /** True once the finger has been inside the ring band at least once. */
  private engaged = false;

  constructor(private readonly root: HTMLElement) {}

  open(profile: RadialProfile): Promise<RadialResult> {
    this.armed = null;
    this.engaged = false;
    this.petals = [];
    this.root.innerHTML = "";
    this.root.hidden = false;

    // Centred horizontally, and high enough that the lowest petal still clears the
    // "Më shumë" bar and the thumb can reach it.
    this.centre = { x: window.innerWidth / 2, y: window.innerHeight * 0.44 };
    const { x, y } = this.centre;

    for (const [i, type] of RADIAL[profile].entries()) {
      const angle = i * 60; // 12, 2, 4, 6, 8, 10 o'clock
      const rad = ((angle - 90) * Math.PI) / 180;
      const el = document.createElement("button");
      el.type = "button";
      el.className = "petal";
      el.style.width = `${PETAL_SIZE}px`;
      el.style.height = `${PETAL_SIZE}px`;
      el.style.margin = `${-PETAL_SIZE / 2}px 0 0 ${-PETAL_SIZE / 2}px`;
      el.style.left = `${x + Math.cos(rad) * PETAL_R}px`;
      el.style.top = `${y + Math.sin(rad) * PETAL_R}px`;
      el.innerHTML =
        `<span class="petal__glyph">${glyphMark(type)}</span>` +
        `<span class="petal__label">${HAZARDS[type].label}</span>`;
      el.addEventListener("click", () => this.finish(type));
      this.root.appendChild(el);
      this.petals.push({ type, el, angle });
    }

    const hub = document.createElement("div");
    hub.className = "hub";
    hub.style.width = `${HUB_R * 2}px`;
    hub.style.height = `${HUB_R * 2}px`;
    hub.style.margin = `${-HUB_R}px 0 0 ${-HUB_R}px`;
    hub.style.left = `${x}px`;
    hub.style.top = `${y}px`;
    hub.textContent = "ANULO";
    hub.addEventListener("click", () => this.finish(null));
    this.root.appendChild(hub);

    const more = document.createElement("button");
    more.type = "button";
    more.className = "more";
    more.textContent = "MË SHUMË";
    more.addEventListener("click", () => this.finish("more"));
    this.root.appendChild(more);

    this.root.addEventListener("click", (ev) => {
      if (ev.target === this.root) this.finish(null);
    }, { once: true });

    return new Promise<RadialResult>((resolve) => { this.resolve = resolve; });
  }

  /** Direction-only selection, live while the finger is inside the ring band. */
  move(x: number, y: number): void {
    const dx = x - this.centre.x;
    const dy = y - this.centre.y;
    const dist = Math.hypot(dx, dy);

    if (dist < HUB_R || dist > BAND_OUTER) {
      this.setArmed(null);
      return;
    }
    this.engaged = true;

    const norm = (((Math.atan2(dy, dx) * 180) / Math.PI + 90) % 360 + 360) % 360;
    let best: HazardType | null = null;
    let bestDelta = Infinity;
    for (const p of this.petals) {
      const delta = Math.abs((((norm - p.angle) % 360) + 540) % 360 - 180);
      if (delta < bestDelta) { bestDelta = delta; best = p.type; }
    }
    this.setArmed(bestDelta <= 30 ? best : null);
  }

  private setArmed(type: HazardType | null): void {
    if (this.armed === type) return;
    this.armed = type;
    for (const p of this.petals) p.el.classList.toggle("petal--armed", p.type === type);
  }

  /**
   * Finger lifted. If a petal is armed, that is the choice. If the finger never
   * reached the ring the menu stays open so it can be tapped instead — closing
   * here would make the tap path impossible.
   */
  release(): void {
    if (this.armed !== null) { this.finish(this.armed); return; }
    if (this.engaged) this.finish(null);
  }

  private finish(result: RadialResult): void {
    if (this.resolve === null) return;
    this.root.hidden = true;
    this.root.innerHTML = "";
    const resolve = this.resolve;
    this.resolve = null;
    this.armed = null;
    this.engaged = false;
    resolve(result);
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }
}

/** The six behind "Më shumë". Never speed-disabled: a blocked report is worse
 *  than a slow one. */
export function overflowTypes(profile: RadialProfile): HazardType[] {
  const shown = new Set<HazardType>(RADIAL[profile]);
  return REPORTABLE_TYPES.filter((t) => !shown.has(t));
}
